import { endpoint } from "@uri/typed-api";
import { z } from "zod/v4";

export const api = {
  log: endpoint({
    authRequired: true,
    input: z.object({
      event: z.object({
        from: z.string().describe("Who sent the message"),
        text: z.string().describe("Message content"),
        time: z.number().describe("Unix timestamp in milliseconds"),
        foreignId: z.string().optional().describe(
          "Client-provided unique message ID for deduplication",
        ),
      }).describe("The message event to log"),
      conversation: z.string().optional().describe(
        "Conversation name. Created automatically if it doesn't exist. Mutually exclusive with conversationId.",
      ),
      conversationId: z.string().optional().describe(
        "Existing conversation ID. Must already exist; returns an error if not. Mutually exclusive with conversation.",
      ),
      groups: z.array(z.string()).optional().describe(
        "Group names to assign. Groups are created automatically if they don't exist. Mutually exclusive with groupIds.",
      ),
      groupIds: z.array(z.string()).optional().describe(
        "Group IDs to assign. Groups must already exist. Mutually exclusive with groups.",
      ),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  createGroup: endpoint({
    authRequired: true,
    input: z.object({
      name: z.string().describe("Name for the new group"),
    }),
    output: z.object({
      ok: z.literal(true),
      groupId: z.string().describe("ID of the created group"),
    }),
  }),
  renameGroup: endpoint({
    authRequired: true,
    input: z.object({
      groupId: z.string().describe("ID of the group to rename"),
      name: z.string().describe("New name for the group"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  addViewer: endpoint({
    authRequired: true,
    input: z.object({
      email: z.string().describe("Email address of the viewer to add"),
      groupId: z.string().describe("ID of the group to add the viewer to"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  getMessages: endpoint({
    authRequired: true,
    input: z.object({
      conversationId: z.string().describe("Conversation ID"),
      since: z.number().optional().describe(
        "Only return messages with time >= this Unix timestamp in milliseconds",
      ),
      until: z.number().optional().describe(
        "Only return messages with time <= this Unix timestamp in milliseconds",
      ),
      limit: z.number().max(200).optional().describe(
        "Maximum number of messages to return (default: 100, max: 200)",
      ),
      offset: z.number().optional().describe(
        "Number of messages to skip for pagination (default: 0)",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      messages: z.array(
        z.object({
          id: z.string().describe("Message ID"),
          from: z.string().describe("Message sender"),
          text: z.string().describe("Message text"),
          time: z.number().describe("Unix timestamp in milliseconds"),
          timeIso: z.string().describe(
            "ISO 8601 timestamp string (UTC), equivalent to `time`",
          ),
        }),
      ).describe("Messages in this conversation, ordered by time ascending"),
    }),
  }),
  getConversations: endpoint({
    authRequired: true,
    input: z.object({
      groupId: z.string().optional().describe("Filter by group ID"),
      limit: z.number().max(100).optional().describe(
        "Maximum number of conversations to return (default: 25, max: 100)",
      ),
      offset: z.number().optional().describe(
        "Number of conversations to skip for pagination (default: 0)",
      ),
      since: z.number().optional().describe(
        "Only return conversations with lastMessageTime >= this Unix timestamp in milliseconds",
      ),
      until: z.number().optional().describe(
        "Only return conversations with lastMessageTime <= this Unix timestamp in milliseconds",
      ),
      participant: z.string().optional().describe(
        "Filter conversations that have messages from this participant (case-insensitive substring match)",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      conversations: z.array(
        z.object({
          id: z.string().describe("Conversation ID"),
          name: z.string().describe("Conversation name"),
          lastMessageTime: z.number().optional().describe(
            "Timestamp of the last message (Unix ms)",
          ),
          lastMessageTimeIso: z.string().optional().describe(
            "ISO 8601 timestamp string (UTC) of the last message, equivalent to `lastMessageTime`",
          ),
          groups: z.array(
            z.object({
              id: z.string().describe("Group ID"),
              name: z.string().describe("Group name"),
            }),
          ).describe("Groups this conversation belongs to"),
        }),
      ).describe("List of conversations for this account"),
    }),
  }),
  getAnalytics: endpoint({
    authRequired: true,
    input: z.object({
      groupId: z.string().optional().describe("Filter by group ID"),
      since: z.number().describe(
        "Only return analytics at or after this Unix timestamp in milliseconds",
      ),
      until: z.number().optional().describe(
        "Only return analytics at or before this Unix timestamp in milliseconds",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      chatsPerDay: z.array(
        z.object({
          date: z.string().describe("UTC day in YYYY-MM-DD format"),
          value: z.number().describe("Conversation count for the day"),
        }),
      ),
      participantsPerDay: z.array(
        z.object({
          date: z.string().describe("UTC day in YYYY-MM-DD format"),
          value: z.number().describe("Unique participant count for the day"),
        }),
      ),
      messagesPerDay: z.array(
        z.object({
          date: z.string().describe("UTC day in YYYY-MM-DD format"),
          value: z.number().describe("Message count for the day"),
        }),
      ),
      latencyByParticipant: z.record(
        z.string(),
        z.array(
          z.object({
            date: z.string().describe("UTC day in YYYY-MM-DD format"),
            avgMs: z.number().describe(
              "Average response latency in milliseconds for this participant on this day",
            ),
            count: z.number().describe(
              "Number of response latency samples for this participant on this day",
            ),
          }),
        ),
      ).describe(
        "Response latency data keyed by participant name, only for the top participants by message count",
      ),
    }),
  }),
  createToken: endpoint({
    authRequired: true,
    input: z.object({
      name: z.string().describe(
        "A label for the token (e.g. 'production', 'staging')",
      ),
      groupIds: z.array(z.string()).optional().describe(
        "Optional group IDs to restrict this token to. If not provided or empty, the token has access to all groups in the account.",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      token: z.string().describe(
        "The full token value to use for API calls",
      ),
      tokenId: z.string().describe("The token record ID"),
    }),
  }),
  listTokens: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      ok: z.literal(true),
      tokens: z.array(
        z.object({
          id: z.string().describe("Token record ID"),
          name: z.string().describe("Token label"),
          createdAt: z.number().describe("Creation timestamp"),
          revokedAt: z.number().optional().describe(
            "Revocation timestamp, if revoked",
          ),
          token: z.string().describe("The full token value"),
          groups: z.array(
            z.object({
              id: z.string().describe("Group ID"),
              name: z.string().describe("Group name"),
            }),
          ).optional().describe("Groups this token is restricted to, if any"),
        }),
      ).describe("All tokens for this account"),
    }),
  }),
  revokeToken: endpoint({
    authRequired: true,
    input: z.object({
      tokenId: z.string().describe("ID of the token to revoke"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  // Personal access tokens: read-only, scoped to the calling user's viewer and
  // admin groups. Authenticated by the dashboard session (InstantDB refresh
  // token) rather than an API token, so a freshly-invited viewer can mint one.
  createUserToken: endpoint({
    authRequired: false,
    input: z.object({
      sessionToken: z.string().describe(
        "InstantDB session (refresh) token of the logged-in user",
      ),
      name: z.string().describe("A label for the token"),
    }),
    output: z.object({
      ok: z.literal(true),
      token: z.string().describe(
        "The full personal access token value. Shown only once — it is not retrievable later.",
      ),
      tokenId: z.string().describe("The token record ID"),
    }),
  }),
  listUserTokens: endpoint({
    authRequired: false,
    input: z.object({
      sessionToken: z.string().describe(
        "InstantDB session (refresh) token of the logged-in user",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      tokens: z.array(
        z.object({
          id: z.string().describe("Token record ID"),
          name: z.string().describe("Token label"),
          createdAt: z.number().describe("Creation timestamp"),
          maskedToken: z.string().describe(
            "Masked token hint (e.g. vc_pat_…a1b2). The full value is never returned after creation.",
          ),
        }),
      ).describe("All personal access tokens for this user"),
    }),
  }),
  deleteUserToken: endpoint({
    authRequired: false,
    input: z.object({
      sessionToken: z.string().describe(
        "InstantDB session (refresh) token of the logged-in user",
      ),
      tokenId: z.string().describe("ID of the token to delete"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  createCheckup: endpoint({
    authRequired: true,
    input: z.object({
      groupId: z.string().describe("ID of the group to monitor"),
      interests: z.string().optional().describe(
        "Description of what to look for in conversations",
      ),
      sampleSize: z.number().describe(
        "Number of conversations to sample per run",
      ),
      frequencyDays: z.number().describe("How often to run, in days"),
      excludeGroupIds: z.array(z.string()).optional().describe(
        "IDs of groups to exclude from this checkup",
      ),
      onlyStartedInPeriod: z.boolean().optional().describe(
        "Only scan conversations that started in this period",
      ),
      githubToken: z.string().optional().describe(
        "Optional GitHub Personal Access Token to let Vivi create issues in your repository",
      ),
      githubRepo: z.string().optional().describe(
        "Optional GitHub repository in owner/repo format (e.g. 'owner/repo')",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      checkupId: z.string().describe("ID of the created checkup"),
    }),
  }),
  updateCheckup: endpoint({
    authRequired: true,
    input: z.object({
      checkupId: z.string().describe("ID of the checkup to update"),
      groupId: z.string().optional().describe("Updated group ID"),
      interests: z.string().optional().describe(
        "Updated description of what to look for",
      ),
      sampleSize: z.number().optional().describe(
        "Updated sample size",
      ),
      frequencyDays: z.number().optional().describe(
        "Updated frequency in days",
      ),
      excludeGroupIds: z.array(z.string()).optional().describe(
        "Updated list of group IDs to exclude",
      ),
      onlyStartedInPeriod: z.boolean().optional().describe(
        "Only scan conversations that started in this period",
      ),
      githubToken: z.string().optional().describe(
        "Optional GitHub Personal Access Token to let Vivi create issues in your repository",
      ),
      githubRepo: z.string().optional().describe(
        "Optional GitHub repository in owner/repo format (e.g. 'owner/repo')",
      ),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  deleteCheckup: endpoint({
    authRequired: true,
    input: z.object({
      checkupId: z.string().describe("ID of the checkup to delete"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  deleteGroup: endpoint({
    authRequired: true,
    input: z.object({
      groupId: z.string().describe("ID of the group to delete"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  runCheckup: endpoint({
    authRequired: true,
    input: z.object({
      checkupId: z.string().describe("ID of the checkup to run now"),
    }),
    output: z.object({ ok: z.literal(true) }),
  }),
  cronStatus: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({
      ok: z.literal(true),
      lastRun: z.any().optional(),
      lastStatus: z.any().optional(),
      lastError: z.any().optional(),
    }),
  }),
  triggerCron: endpoint({
    authRequired: true,
    input: z.object({}),
    output: z.object({ ok: z.literal(true) }),
  }),
  translate: endpoint({
    authRequired: true,
    input: z.object({
      messages: z.array(
        z.object({
          id: z.string().describe("Message ID"),
          text: z.string().describe("Message text to translate"),
        }),
      ).describe("Messages to translate to English"),
    }),
    output: z.object({
      ok: z.literal(true),
      translations: z.array(
        z.object({
          id: z.string().describe("Message ID"),
          translatedText: z.string().describe("Translated text"),
        }),
      ).describe("Translations in the same order as input"),
    }),
  }),
  listReports: endpoint({
    authRequired: true,
    input: z.object({
      checkupId: z.string().optional().describe(
        "Filter reports by checkup ID",
      ),
      limit: z.number().max(100).optional().describe(
        "Maximum number of reports to return (default: 20)",
      ),
    }),
    output: z.object({
      ok: z.literal(true),
      reports: z.array(
        z.object({
          id: z.string().describe("Report ID"),
          content: z.string().describe("Markdown report content"),
          createdAt: z.number().describe("Creation timestamp"),
          conversationIds: z.string().describe(
            "JSON array of sampled conversation IDs",
          ),
          checkup: z.any().optional().describe("Associated checkup data"),
        }),
      ).describe("Reports ordered by creation date descending"),
    }),
  }),
};
